import dayjs from 'dayjs';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    FormHelperText,
    FormLabel,
    Grid,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
    Divider,
    Stack,
    Chip,
    alpha,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
    AccountBalance,
    CurrencyRupee,
    DescriptionOutlined,
    CloudUploadOutlined,
    PaymentsOutlined,
    ReceiptLongOutlined,
    NotesOutlined,
    CalendarMonthOutlined,
    Close,
} from '@mui/icons-material';

import { RootState } from '../../../../../../../../../redux/store';
import { closeDrawer } from '../../../../../../../../../redux/reducer/drawerSlice';
import { showSnackbar } from '../../../../../../../../../redux/reducer/snackbarSlice';
import { setModuleRefresh } from '../../../../../../../../../redux/reducer/registration/RefreshSlice';
import { StoreBeneficiaryDisbursmentService } from '../../../../../../../../../utils/services/collaboration-agreement/collaboration-agreement.service';
import { BeneficiaryStatus, UserRole } from '../../../../../../../../../utils/collaboration-agreement-actions';

const DISBURSEMENT_SOURCE_OPTIONS = [
    { value: 'donor', label: 'Disbursement from Donor' },
    { value: 'cmrf', label: 'Disbursement from CMRF' },
];

const validationSchema = Yup.object({
    disbursement_date: Yup.string().required('Disbursement date is required'),
    amount: Yup.number()
        .typeError('Amount must be a valid number')
        .required('Amount is required')
        .min(1, 'Amount must be greater than 0'),
    utr_no: Yup.string().trim().required('UTR No is required'),
    payment_mode: Yup.string().required('Payment mode is required'),
    // type: Yup.string().required('Type is required'),
    remark: Yup.string().trim().required('Remark is required'),
});

const PAYMENT_MODES = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'Cheque', 'DD'];
// const TYPES = ['NEFT', 'RTGS', 'IMPS', 'cmrf'];

const requiredStar = (
    <Box component="span" sx={{ color: 'error.main', ml: 0.3 }}>
        *
    </Box>
);

const fieldLabelSx = {
    mb: 0.75,
    display: 'inline-block',
    fontSize: 13,
    fontWeight: 700,
    color: 'text.primary',
};

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        bgcolor: 'background.paper',
        transition: '0.2s ease',
        '&:hover': {
            bgcolor: '#fff',
        },
        '&.Mui-focused': {
            bgcolor: '#fff',
        },
        '& .MuiInputAdornment-positionStart': {
            pl: 1.5,
            pr: 0.5,
        },
        '& .MuiInputAdornment-positionEnd': {
            pr: 1.5,
            pl: 0.5,
        },
    },
};

const sectionCardSx = {
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
    overflow: 'hidden',
};

const AddDisbursmentDrawer: React.FC = () => {
    const dispatch = useDispatch();

    const { drawerData } = useSelector((state: RootState) => state.drawer);
    const refreshFlag = useSelector(
        (state: RootState) => state.refreshSlice.beneficiary_disbursment
    );
    const userDetailsRefreshFlag = useSelector(
        (state: RootState) => state.refreshSlice.user_details
    );
    const authuser = useSelector((state: RootState) => state.authUser.userDetails);
    const userType = authuser?.user_type || '';
    const isDonor = userType === UserRole.DONOR;
    const isHospital = userType === UserRole.HOSPITAL;
    const isDonorAndHospital = isDonor || isHospital;
    const [isLoading, setIsLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            disbursement_date: dayjs().format('YYYY-MM-DD'),
            amount: '',
            utr_no: '',
            remark: '',
            payment_mode: 'NEFT',
            // type: 'NEFT',
            updated_by_status: isDonorAndHospital ? 'donor' : 'cmrf',
            document: null as File | null,
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                setIsLoading(true);
    
                const formData = new FormData();
                const status = values.updated_by_status === "donor" ? null : BeneficiaryStatus.DISBURSEMENT_DONE_BY_CMRF;

                formData.append('tri_party_uuid', drawerData?.tri_party_uuid || '');
                formData.append('beneficiary_uuid', drawerData?.beneficiary_uuid || '');
                formData.append(
                    'disbursement_date',
                    dayjs(values.disbursement_date).format('YYYY-MM-DD')
                );
                formData.append('amount', values.amount.toString());
                formData.append('utr_no', values.utr_no.trim());
                formData.append('remark', values.remark.trim());
                formData.append('payment_mode', values.payment_mode);
                // formData.append('type', values.type);
                formData.append('updated_by_status', values.updated_by_status);
                if (status) {
                    formData.append('status', status);
                }

                if (values.document) {
                    formData.append('document', values.document);
                }

                const { code, message } = await StoreBeneficiaryDisbursmentService(formData);

                if (code === 200) {
                    dispatch(
                        showSnackbar({
                            type: 'success',
                            message: message || 'Disbursement added successfully',
                        })
                    );

                    dispatch(
                        setModuleRefresh({
                            moduleName: 'beneficiary_disbursment',
                            refresh: !refreshFlag,
                        })
                    );
                    dispatch(
                        setModuleRefresh({
                            moduleName: 'user_details',
                            refresh: !userDetailsRefreshFlag,
                        })
                    );

                    dispatch(closeDrawer());
                } else {
                    dispatch(
                        showSnackbar({
                            type: 'error',
                            message: message || 'Something went wrong',
                        })
                    );
                }
            } catch (error) {
                dispatch(
                    showSnackbar({
                        type: 'error',
                        message: 'Something went wrong while saving disbursement',
                    })
                );
            } finally {
                setIsLoading(false);
            }
        },
    });

    const selectedFile = formik.values.document as File | null;

    return (
        <FormikProvider value={formik}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box
                    component="form"
                    onSubmit={formik.handleSubmit}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 'calc(100vh - 59px)',
                        maxHeight: 'calc(100vh - 59px)',
                        bgcolor: '#f8fafc',
                    }}
                >
                    {/* Header */}


                    {/* Body */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            p: 2.5,
                        }}
                    >
                        <Paper sx={sectionCardSx}>
                            <Box
                                sx={{
                                    p: 2,
                                    background:
                                        'linear-gradient(135deg, rgba(46,125,50,0.08), rgba(2,132,199,0.06))',
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <PaymentsOutlined color="success" fontSize="small" />
                                    <Typography fontWeight={800}>Payment Information</Typography>
                                </Stack>
                            </Box>

                            <Divider />

                            <Box sx={{ p: 2 }}>
                                <Grid container spacing={2}>
                                    {/* Disbursement Date */}
                                    <Grid item xs={12} sm={6}>
                                        <FormLabel sx={fieldLabelSx}>
                                            Disbursement Date {requiredStar}
                                        </FormLabel>

                                        <DatePicker
                                            value={
                                                formik.values.disbursement_date
                                                    ? dayjs(formik.values.disbursement_date)
                                                    : null
                                            }
                                            onChange={(val) =>
                                                formik.setFieldValue(
                                                    'disbursement_date',
                                                    val ? val.format('YYYY-MM-DD') : ''
                                                )
                                            }
                                            format="DD-MM-YYYY"
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    size: 'small',
                                                    name: 'disbursement_date',
                                                    onBlur: formik.handleBlur,
                                                    placeholder: 'Select date',
                                                    error:
                                                        formik.touched.disbursement_date &&
                                                        Boolean(formik.errors.disbursement_date),
                                                    helperText:
                                                        formik.touched.disbursement_date &&
                                                        formik.errors.disbursement_date,
                                                    InputProps: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <CalendarMonthOutlined fontSize="small" />
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                    sx: inputSx,
                                                },
                                            }}
                                        />
                                    </Grid>

                                    {/* Amount */}
                                    <Grid item xs={12} sm={6}>
                                        <FormLabel sx={fieldLabelSx}>
                                            Amount {requiredStar}
                                        </FormLabel>

                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="amount"
                                            placeholder="Enter amount"
                                            value={formik.values.amount}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d*$/.test(value)) {
                                                    formik.setFieldValue('amount', value);
                                                }
                                            }}
                                            onBlur={formik.handleBlur}
                                            inputProps={{
                                                inputMode: 'numeric',
                                                style: { textAlign: 'right' },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CurrencyRupee fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            error={
                                                formik.touched.amount &&
                                                Boolean(formik.errors.amount)
                                            }
                                            helperText={
                                                formik.touched.amount && formik.errors.amount
                                            }
                                            sx={inputSx}
                                        />
                                    </Grid>

                                    {/* UTR No */}
                                    <Grid item xs={12} sm={6}>
                                        <FormLabel sx={fieldLabelSx}>
                                            UTR No {requiredStar}
                                        </FormLabel>

                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="utr_no"
                                            placeholder="Enter UTR number"
                                            value={formik.values.utr_no}
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    'utr_no',
                                                    e.target.value.toUpperCase()
                                                )
                                            }
                                            onBlur={formik.handleBlur}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <ReceiptLongOutlined fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            error={
                                                formik.touched.utr_no &&
                                                Boolean(formik.errors.utr_no)
                                            }
                                            helperText={
                                                formik.touched.utr_no && formik.errors.utr_no
                                            }
                                            sx={inputSx}
                                        />
                                    </Grid>

                                    {/* Payment Mode */}
                                    <Grid item xs={12} sm={6}>
                                        <FormLabel sx={fieldLabelSx}>
                                            Payment Mode {requiredStar}
                                        </FormLabel>

                                        <Select
                                            fullWidth
                                            size="small"
                                            name="payment_mode"
                                            value={formik.values.payment_mode}
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    'payment_mode',
                                                    e.target.value
                                                )
                                            }
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.payment_mode &&
                                                Boolean(formik.errors.payment_mode)
                                            }
                                            sx={{
                                                ...inputSx,
                                                borderRadius: 2,
                                                bgcolor: 'background.paper',
                                            }}
                                        >
                                            {PAYMENT_MODES.map((mode) => (
                                                <MenuItem key={mode} value={mode}>
                                                    {mode}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        {formik.touched.payment_mode &&
                                            formik.errors.payment_mode && (
                                                <FormHelperText error>
                                                    {formik.errors.payment_mode}
                                                </FormHelperText>
                                            )}
                                    </Grid>

                                    {/* Type */}
                                    {/* <Grid item xs={12} sm={6}>
                                        <FormLabel sx={fieldLabelSx}>
                                            Type {requiredStar}
                                        </FormLabel>

                                        <Select
                                            fullWidth
                                            size="small"
                                            name="type"
                                            value={formik.values.type}
                                            onChange={(e) =>
                                                formik.setFieldValue('type', e.target.value)
                                            }
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.type &&
                                                Boolean(formik.errors.type)
                                            }
                                            sx={{
                                                ...inputSx,
                                                borderRadius: 2,
                                                bgcolor: 'background.paper',
                                            }}
                                        >
                                            {TYPES.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type.toUpperCase()}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        {formik.touched.type && formik.errors.type && (
                                            <FormHelperText error>
                                                {formik.errors.type}
                                            </FormHelperText>
                                        )}
                                    </Grid> */}

                                    {/* Disbursement Source (only for verifier roles) */}
                                    {!isDonorAndHospital && (
                                        <Grid item xs={12} sm={6}>
                                            <FormLabel sx={fieldLabelSx}>
                                                Disbursement Source {requiredStar}
                                            </FormLabel>

                                            <Select
                                                fullWidth
                                                size="small"
                                                name="updated_by_status"
                                                value={formik.values.updated_by_status}
                                                onChange={(e) =>
                                                    formik.setFieldValue(
                                                        'updated_by_status',
                                                        e.target.value
                                                    )
                                                }
                                                onBlur={formik.handleBlur}
                                                sx={{
                                                    ...inputSx,
                                                    borderRadius: 2,
                                                    bgcolor: 'background.paper',
                                                }}
                                            >
                                                {DISBURSEMENT_SOURCE_OPTIONS.map((opt) => (
                                                    <MenuItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                    )}

                                    {/* Remark */}
                                    <Grid item xs={12} sm={6}>
                                        <FormLabel sx={fieldLabelSx}>
                                            Remark {requiredStar}
                                        </FormLabel>

                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="remark"
                                            placeholder="Enter remark"
                                            value={formik.values.remark}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <NotesOutlined fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            error={
                                                formik.touched.remark &&
                                                Boolean(formik.errors.remark)
                                            }
                                            helperText={
                                                formik.touched.remark && formik.errors.remark
                                            }
                                            sx={inputSx}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>

                        {/* Document Upload */}
                        <Paper sx={{ ...sectionCardSx, mt: 2 }}>
                            <Box sx={{ p: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <DescriptionOutlined color="primary" fontSize="small" />
                                    <Box>
                                        <Typography fontWeight={800}>Payment Document</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Upload receipt, cheque copy, transaction proof, or PDF
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Box
                                    component="label"
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        minHeight: 110,
                                        borderRadius: 3,
                                        border: '1.5px dashed',
                                        borderColor: selectedFile ? 'success.main' : 'divider',
                                        bgcolor: selectedFile
                                            ? 'rgba(46,125,50,0.06)'
                                            : 'rgba(15,23,42,0.02)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        transition: '0.2s ease',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: 'rgba(25,118,210,0.05)',
                                        },
                                    }}
                                >
                                    <input
                                        hidden
                                        type="file"
                                        accept="image/*,application/pdf"
                                        onChange={(e) =>
                                            formik.setFieldValue(
                                                'document',
                                                e.target.files?.[0] || null
                                            )
                                        }
                                    />

                                    <Stack spacing={1} alignItems="center">
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: 2.5,
                                                display: 'grid',
                                                placeItems: 'center',
                                                bgcolor: 'background.paper',
                                                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                                                color: selectedFile
                                                    ? 'success.main'
                                                    : 'primary.main',
                                            }}
                                        >
                                            <CloudUploadOutlined />
                                        </Box>

                                        {selectedFile ? (
                                            <>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={700}
                                                    sx={{
                                                        maxWidth: 320,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {selectedFile.name}
                                                </Typography>

                                                <Chip
                                                    size="small"
                                                    color="success"
                                                    label={`${Math.ceil(
                                                        selectedFile.size / 1024
                                                    )} KB selected`}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <Typography variant="body2" fontWeight={700}>
                                                    Click to upload document
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Supported formats: Image or PDF
                                                </Typography>
                                            </>
                                        )}
                                    </Stack>
                                </Box>

                                {selectedFile && (
                                    <Stack
                                        direction="row"
                                        justifyContent="flex-end"
                                        sx={{ mt: 1 }}
                                    >
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                                formik.setFieldValue('document', null)
                                            }
                                        >
                                            Remove file
                                        </Button>
                                    </Stack>
                                )}
                            </Box>
                        </Paper>
                    </Box>

                    {/* Footer */}
                    <DialogActions
                        sx={{
                            px: 2.5,
                            py: 1.75,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 2,
                            boxShadow: '0 -8px 24px rgba(15, 23, 42, 0.06)',
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={() => dispatch(closeDrawer())}
                            disabled={isLoading}
                            sx={{
                                borderRadius: 2,
                                px: 2.5,
                                textTransform: 'none',
                                fontWeight: 700,
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            color="success"
                            disabled={isLoading}
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : null
                            }
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                textTransform: 'none',
                                fontWeight: 800,
                                boxShadow: '0 8px 18px rgba(46,125,50,0.25)',
                            }}
                        >
                            {isLoading ? 'Saving...' : 'Save Disbursement'}
                        </Button>
                    </DialogActions>
                </Box>
            </LocalizationProvider>
        </FormikProvider>
    );
};

export default AddDisbursmentDrawer;